import { Atom, ATOMS } from "../tools/atoms";
import { MolecularFormulaParser } from "../tools/molecular-formula.parser";

export class ToolsService {
    /**
     * Unified atomic mass in [kg]
     */
    public readonly DALTON: number = 1.6605390666e-27;
    /**
     * Molar mass constant in [g/mol]
     */
    public readonly Mu: number = 1;

    public molarMassFromMolecularFormula(formula: string): number {
        if (typeof formula !== "string" || formula.length === 0) {
            return 0;
        }

        const parser = new MolecularFormulaParser(formula);

        const atoms = [];
        while (!parser.isEnd()) {
            if (parser.nextIsAsciiUpper()) {
                atoms.push(parser.readAtomWithCount());
            } else {
                throw new Error("");
            }
        }

        // console.log( atoms.map( a => `${a.count}x ${a.atom.letter}` ).join( ', ' ) );

        // Sum up all atom weights multiplied by their count
        return atoms.reduce(
            (sum, a) => sum + this.atomWeight(a.atom) * a.count,
            0,
        );
    }

    public atomWeight(atom: Atom): number {
        if (
            typeof atom.conventionalWeight === "number" &&
            0 < atom.conventionalWeight
        ) {
            return atom.conventionalWeight;
        } else {
            // weight of first, abundant isotope
            return atom.info.isotopes[0].weight;
        }
    }

    public findAtom(
        nameOrAbbr: string,
        lang?: "en" | "de" | "fr",
    ): Atom | undefined {
        if (typeof nameOrAbbr !== "string" || nameOrAbbr.length === 0) {
            return undefined;
        }

        // make lowercase
        nameOrAbbr = nameOrAbbr.toLowerCase();

        // try to find by exact match of symbol
        let atom: Atom | undefined = ATOMS.find(
            (a) => a.letter.toLowerCase() === nameOrAbbr,
        );

        if (atom) {
            return atom;
        }

        // try to find by title (localized) or name (lowercase, english)
        atom = ATOMS.find((a) => {
            const title =
                lang && lang in a.title
                    ? a.title[lang].toLowerCase()
                    : undefined;
            const name = a.name.toLowerCase();
            return (
                (title && title.indexOf(nameOrAbbr) === 0) ||
                name.indexOf(nameOrAbbr) === 0
            );
        });

        return atom || undefined;
    }
}
